import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

// Constants
const POSTAL_CODE_REGEX = /^[a-zA-Z0-9\s-]{3,10}$/;
const HTTP_STATUS = {
  UNPROCESSABLE_ENTITY: 422
};

/**
 * A custom element that manages country and province selection for shipping forms.
 *
 * @typedef {object} CountryProvinceRefs
 * @property {HTMLSelectElement} countryInput - The country select input.
 * @property {HTMLSelectElement} provinceInput - The province select input.
 *
 * @extends Component<CountryProvinceRefs>
 */
export class CountryProvinceComponent extends Component {
  requiredRefs = ["countryInput", "provinceInput"];

  /** @type {string | null} */
  #defaultCountry = null;

  /** @type {string | null} */
  #defaultProvince = null;

  /** @type {boolean} */
  #hasPopulatedOptions = false;

  /** @type {MutationObserver | null} */
  #dialogObserver = null;

  /** @type {string | null} */
  #currentSelectedCountry = null;

  /** @type {string | null} */
  #currentSelectedProvince = null;

  get container() {
    return this.closest('.dialog') || this.closest('details');
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize with default country if provided
    this.#initDefaultCountry();

    // Handle country change
    this.boundOnCountryChange = this.#onCountryChange.bind(this);
    this.refs.countryInput.addEventListener("change", this.boundOnCountryChange);

    // Use MutationObserver to detect when container (dialog or details) opens
    this.#setupContainerObserver();

    this.boundOnCartUpdate = this.#onCartUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.boundOnCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.boundOnCartUpdate) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.boundOnCartUpdate);
    }
    if (this.boundOnCountryChange) {
      this.refs.countryInput.removeEventListener("change", this.boundOnCountryChange);
    }

    this.#dialogObserver?.disconnect();
    this.#dialogObserver = null;
  }

  /**
   * Handle container open (dialog or details) to populate country options
   */
  #onContainerOpen() {
    if (this.#hasPopulatedOptions) return;

    if (this.container && (this.container.open || this.container.hasAttribute('open'))) {
      this.#ensureCountryOptions();
      this.#hasPopulatedOptions = true;

      if (this.#defaultCountry) {
        this.#applyDefaultCountry(this.#defaultCountry, this.#defaultProvince);
        this.#defaultCountry = null;
        this.#defaultProvince = null;
      }
    }
  }

  /**
   * Initialize the country selection based on data attributes
   */
  #initDefaultCountry() {
    this.#defaultCountry = this.dataset.country;
    this.#defaultProvince = this.dataset.province;
  }

  /**
   * Populate country options from <template> (lazy loading)
   */
  #renderCountryOptionsFromTemplate() {
    const template = this.refs.countryInput.querySelector("template");
    if (template) {
      const templateContent = document.importNode(template.content, true);
      this.refs.countryInput.appendChild(templateContent);
    }
  }

  /**
   * Ensure country options are populated before handling change
   */
  #ensureCountryOptions() {
    const template = this.refs.countryInput.querySelector("template");
    if (template) {
      this.#renderCountryOptionsFromTemplate();
    }
  }

  /**
   * Set default country and province after options are populated
   * @param {string} countryData - Country name
   * @param {string} provinceData - Province name
   */
  #applyDefaultCountry(countryData, provinceData) {
    const countryOption = Array.from(this.refs.countryInput.options).find(
      (option) => option.textContent === countryData
    );

    if (countryOption) {
      this.refs.countryInput.selectedIndex = countryOption.index;
      this.refs.countryInput.dispatchEvent(new Event("change"));
    }
  }

  /**
   * Setup MutationObserver to detect when container (dialog or details) opens
   */
  #setupContainerObserver() {
    if (!this.container) return;

    this.#dialogObserver = new MutationObserver((mutations) => {
      // Use some() for early exit when we find the mutation we're looking for
      mutations.some((mutation) => {
        const containerOpen = this.container?.open || this.container?.hasAttribute('open');
        if (mutation.type === "attributes" &&
            mutation.attributeName === "open" &&
            containerOpen) {
          this.#onContainerOpen();
          return true; // Stop processing other mutations
        }
        return false;
      });
    });

    this.#dialogObserver.observe(this.container, {
      attributes: true,
      attributeFilter: ["open"],
    });
  }

  /**
   * Handles country selection change
   * Updates province options based on selected country
   * Stores current selection for restoration after DOM morph
   */
  #onCountryChange() {
    const selectedOption = this.refs.countryInput.options[this.refs.countryInput.selectedIndex];
    if (!selectedOption) return;

    // Store current selection for restoration after DOM morph
    this.#currentSelectedCountry = selectedOption.value;
    this.#currentSelectedProvince = null; // Reset province when country changes

    const provincesData = selectedOption.dataset.provinces;
    if (!provincesData) {
      this.#toggleProvinceField(false);
      return;
    }

    try {
      const provinces = JSON.parse(provincesData);
      if (!Array.isArray(provinces) || provinces.length === 0) {
        this.#toggleProvinceField(false);
        return;
      }

      this.#toggleProvinceField(true);
      this.#renderProvinceOptions(provinces);
    } catch (error) {
      console.error("Error parsing provinces data:", error);
      this.#toggleProvinceField(false);
      // Could emit a custom event here for better error handling
      this.dispatchEvent(new CustomEvent('province-parse-error', {
        detail: { error, provincesData },
        bubbles: true
      }));
    }
  }

  /**
   * Toggle visibility of the province field
   * @param {boolean} isVisible
   */
  #toggleProvinceField(isVisible) {
    const provinceField = this.refs.provinceInput.closest(".cart-shipping__field");
    if (provinceField) {
      provinceField.hidden = !isVisible;
    }
  }

  /**
   * Update province options based on selected country
   * @param {Array<Array<string>>} provinces - Array of province data [code, name]
   */
  #renderProvinceOptions(provinces) {
    // Clear existing options more efficiently
    this.refs.provinceInput.replaceChildren();

    // Use DocumentFragment for better performance when adding multiple options
    const fragment = document.createDocumentFragment();

    provinces.forEach(([code, name]) => {
      const isSelected = code === this.dataset.province || code === this.#currentSelectedProvince;
      const option = new Option(name, code, isSelected, isSelected);
      fragment.appendChild(option);

      // Store selected province for restoration
      if (isSelected) {
        this.#currentSelectedProvince = code;
      }
    });

    this.refs.provinceInput.appendChild(fragment);
  }

  /**
   * Handle cart update events to reinitialize after DOM morph
   * @param {Event} event - Cart update event
   */
  #onCartUpdate(event) {
    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      this.#reinitializeAfterMorph();
    });
  }

  /**
   * Reinitialize component after DOM morph
   */
  #reinitializeAfterMorph() {
    // Check if options are still populated
    if (this.refs.countryInput.options.length <= 1) {
      // Options were cleared, need to repopulate
      this.#hasPopulatedOptions = false;
      this.#ensureCountryOptions();
      this.#hasPopulatedOptions = true;
    }

    // Restore previous selections if they exist
    if (this.#currentSelectedCountry) {
      this.#restoreSelections();
    } else if (this.#defaultCountry) {
      // Fallback to default values
      this.#applyDefaultCountry(this.#defaultCountry, this.#defaultProvince);
    }
  }

  /**
   * Restore previous country and province selections
   */
  #restoreSelections() {
    // Restore country selection
    const countryOption = Array.from(this.refs.countryInput.options).find(
      (option) => option.value === this.#currentSelectedCountry
    );

    if (countryOption) {
      this.refs.countryInput.selectedIndex = countryOption.index;

      // Trigger country change to populate provinces
      this.refs.countryInput.dispatchEvent(new Event("change"));

      // Restore province selection after a short delay to allow provinces to load
      if (this.#currentSelectedProvince) {
        setTimeout(() => {
          const provinceOption = Array.from(this.refs.provinceInput.options).find(
            (option) => option.value === this.#currentSelectedProvince
          );

          if (provinceOption) {
            this.refs.provinceInput.selectedIndex = provinceOption.index;
          }
        }, 50);
      }
    }
  }

  /**
   * Called by morph system after DOM update
   * This is automatically called by the morph system
   */
  updatedCallback() {
    this.#reinitializeAfterMorph();
  }
}

if (!customElements.get("country-province-component")) {
  customElements.define("country-province-component", CountryProvinceComponent);
}

/**
 * A custom element that manages shipping calculation form.
 *
 * @typedef {object} CartShippingRefs
 * @property {HTMLFormElement} form - The shipping calculation form.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} results - The results container.
 * @property {HTMLInputElement} zipInput - The zip code input.
 * @property {CountryProvinceComponent} countryProvince - The country-province component.
 *
 * @extends Component<CartShippingRefs>
 */
export class CartShippingComponent extends Component {
  requiredRefs = ["form", "submitButton", "results", "zipInput", "countryProvince"];

  /** @type {boolean} */
  #isSubmitting = false;

  /** @type {AbortController | null} */
  #currentFetchController = null;

  /** @type {string} */
  #defaultErrorMessage = Theme.translations.shipping_calculator_error;

  connectedCallback() {
    super.connectedCallback();
    this.refs.form.addEventListener("submit", this.#onFormSubmit);
    this.boundInvalidateDelivery = () => { this.#currentFetchController?.abort(); clearDeliveryEstimate(); this.refs.results.hidden = true; };
    this.refs.form.addEventListener("input", this.boundInvalidateDelivery);
    this.refs.form.addEventListener("change", this.boundInvalidateDelivery);

    if (Shopify?.designMode) {
      this.refs.submitButton.type = "button";
      this.boundHandleDesignModeSubmit = this.#handleDesignModeSubmit.bind(this);
      this.refs.submitButton.addEventListener("click", this.boundHandleDesignModeSubmit);
    }

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.form.removeEventListener("submit", this.#onFormSubmit);
    this.refs.form.removeEventListener("input", this.boundInvalidateDelivery);
    this.refs.form.removeEventListener("change", this.boundInvalidateDelivery);
    if (this.boundOnCountryChange && this.refs.countryInput) {
      this.refs.countryInput.removeEventListener("change", this.boundOnCountryChange);
    }
    if (this.boundHandleDesignModeSubmit) {
      this.refs.submitButton.removeEventListener("click", this.boundHandleDesignModeSubmit);
    }
    if (this.boundOnCartUpdate) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.boundOnCartUpdate);
    }
  }

  /**
   * Handle form submission for shipping calculation
   * @param {Event} event - The submit event
   */
  #onFormSubmit = (event) => {
    event?.preventDefault();

    if (this.#isSubmitting) return;

    const formData = this.#extractFormData();
    const validationError = this.#validateForm(formData.zip, formData.country);

    if (validationError) {
      this.#renderErrors([validationError]);
      this.refs.results.hidden = false;
      return;
    }

    this.#submitShippingRequest(formData);
  }

  /**
   * Handles form submission in Shopify design mode
   * @param {Event} event - Click event
   */
  #handleDesignModeSubmit = (event) => {
    event.preventDefault();
    this.#onFormSubmit(event);
  };

  /**
   * Extract form data from inputs
   * @returns {Object} Form data object
   */
  #extractFormData() {
    return {
      zip: this.refs.zipInput.value.trim(),
      country: this.refs.countryProvince.refs.countryInput.value,
      province: this.refs.countryProvince.refs.provinceInput.value
    };
  }

  /**
   * Submit shipping calculation request
   * @param {Object} formData - Form data object
   */
  async #submitShippingRequest(formData) {
    this.#isSubmitting = true;
    clearDeliveryEstimate();
    const estimateRevision = deliveryEstimateRevision;
    this.refs.results.replaceChildren();
    this.#setLoadingState();


    // Abort previous fetch if still pending
    this.#currentFetchController?.abort();

    // Create new controller for this specific fetch
    this.#currentFetchController = new AbortController();

    try {
      const { response, data } = await requestDeliveryRates(formData, this.#currentFetchController.signal);

      if (!response.ok) {
        if (response.status === HTTP_STATUS.UNPROCESSABLE_ENTITY) {
          this.#handleServerErrors(data);
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } else if (data.shipping_rates) {
        const cartResponse = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, { cache: "no-store", signal: this.#currentFetchController.signal });
        if (!cartResponse.ok) throw new Error(this.#defaultErrorMessage);
        const currentCart = await cartResponse.json();
        if (estimateRevision !== deliveryEstimateRevision) return;
        this.#renderShippingRates(data.shipping_rates, currentCart, formData);
      } else {
        this.#handleServerErrors(data);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Shipping calculation error:", error);
      this.#renderErrors([this.#defaultErrorMessage]);
    } finally {
      this.#isSubmitting = false;
      this.#clearLoadingState();
      this.refs.results.hidden = false;
      // Clear the reference after fetch completes
      this.#currentFetchController = null;
    }
  }

  /**
   * Validate form data
   * @param {string} zip - Postal code
   * @param {string} country - Country code
   * @returns {string|null} Error message or null if valid
   */
  #validateForm(zip, country) {
    if (!zip) return Theme.translations.please_enter_a_postal_code;
    if (!country) return Theme.translations.please_select_a_country;
    if (!POSTAL_CODE_REGEX.test(zip)) {
      return Theme.translations.please_enter_a_valid_postal_code;
    }
    return null;
  }

  /**
   * Add loading state to submit button
   */
  #setLoadingState() {
    this.refs.submitButton.classList.add("btn--loading");
    this.refs.submitButton.disabled = true;
  }

  /**
   * Remove loading state from submit button
   */
  #clearLoadingState() {
    this.refs.submitButton.classList.remove("btn--loading");
    this.refs.submitButton.disabled = false;
  }

  /**
   * Render generic error messages
   */
  #renderErrors(errorMessages) {
    this.refs.results.innerHTML = `
      <div class="form__error-list" role="alert" aria-live="polite">
        <div class="form__error">
          <span class="svg-icon">
            ${ Theme.icons.warning }
          </span>
          ${Theme.translations.shipping_calculator_error_message}
        </div>
        ${
          `<ul class="list-disc" role="list">${errorMessages.map((msg) => `<li>${this.#escapeHtml(msg)}</li>`).join("")}</ul>`
        }
      </div>
    `;
  }

  /**
   * Render success messages
   */
  #renderSuccess(messageLabel, messagesList) {
    this.refs.results.innerHTML = `
      <div class="form__success-list" role="alert" aria-live="polite">
        <div class="form__success">
          <span class="svg-icon">
            ${ Theme.icons.info }
          </span>
          ${messageLabel}
        </div>
        ${
          `<ul class="list-disc" role="list">${messagesList}</ul>`
        }
      </div>
    `;
  }

  /**
   * Handles server error responses
   * Extracts error messages and renders them
   * @param {Object} response - Error response object
   */
  #handleServerErrors(response) {
    const errorMessages = this.#extractErrorMessages(response);
    this.#renderErrors(errorMessages);
  }

  /**
   * Extracts error messages from various response formats
   * Handles string, object with message, field errors, and errors array
   * @param {Object|string} response - Error response
   * @returns {string[]} Array of error messages
   */
  #extractErrorMessages(response) {
    const errorMessages = [];

    if (typeof response === "string") {
      errorMessages.push(response);
      return errorMessages;
    }

    if (response.message) {
      errorMessages.push(response.message);
      return errorMessages;
    }

    // Check for field-level errors
    const hasFieldErrors = Object.keys(response).some((key) => Array.isArray(response[key]));
    if (hasFieldErrors) {
      Object.entries(response).forEach(([field, messages]) => {
        if (Array.isArray(messages)) {
          errorMessages.push(...messages);
        } else {
          errorMessages.push(messages);
        }
      });
      return errorMessages;
    }

    // Check for errors object
    if (response.errors) {
      Object.entries(response.errors).forEach(([field, messages]) => {
        if (Array.isArray(messages)) {
          errorMessages.push(...messages);
        } else {
          errorMessages.push(messages);
        }
      });
      return errorMessages;
    }

    // Fallback to default error message
    errorMessages.push(this.#defaultErrorMessage);
    return errorMessages;
  }

  /**
   * Renders shipping rates in results container
   * Formats rates with proper currency and handles empty rates
   * @param {Array} shippingRates - Array of shipping rate objects
   */
  #renderShippingRates(shippingRates, cart, address) {
    const rates = normaliseDeliveryRates(shippingRates, cart.currency);
    if (!rates.length) {
      clearDeliveryEstimate();
      this.#renderErrors([Theme.translations.shipping_calculator_not_found]);
      return;
    }
    const formatter = deliveryMoney(cart.currency);
    const choices = document.createElement('fieldset');
    choices.className = 'cart-shipping__rate-choices';
    const legend = document.createElement('legend');
    legend.textContent = this.dataset.rateHeading;
    choices.append(legend);
    const group = `delivery-rate-${++deliveryRateGroup}`;
    rates.forEach((rate, index) => {
      const label = document.createElement('label');
      label.className = 'cart-shipping__rate-choice';
      const input = document.createElement('input');
      input.type = 'radio'; input.name = group; input.value = String(index); input.checked = index === 0;
      const title = document.createElement('span'), amount = document.createElement('strong');
      title.textContent = rate.name; amount.textContent = formatter(rate.price);
      input.addEventListener('change', () => { if (input.checked) setDeliveryEstimate(rate, cart, address); });
      label.append(input, title, amount); choices.append(label);
    });
    this.refs.results.replaceChildren(choices);
    setDeliveryEstimate(rates[0], cart, address);
  }

  /**
   * Escapes HTML special characters to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Returns localized shipping message based on rate count
   * @param {number} count - Number of shipping rates
   * @returns {string} Localized message
   */
  #shippingMessageForCount(count) {
    if (count === 1) {
      return Theme.translations.shipping_calculator_one_result;
    }
    const message = Theme.translations.shipping_calculator_multiple_results;
    return message.includes("{{ count }}") ? message.replace("{{ count }}", count.toString()) : message;
  }


  /**
   * Called by morph system after DOM update
   * This is automatically called by the morph system
   */
  updatedCallback() {
    // Ensure country-province component is properly reinitialized
    if (this.refs.countryProvince && typeof this.refs.countryProvince.updatedCallback === 'function') {
      this.refs.countryProvince.updatedCallback();
    }
  }
}



const deliveryEstimateKey = 'olouioo:delivery-estimate:v1';
const deliveryEstimateLifetime = 10 * 60 * 1000;
let deliveryRateGroup = 0;
let deliveryEstimateRevision = 0;
let selectedDeliveryEstimate = null;
try {
  const saved = JSON.parse(sessionStorage.getItem(deliveryEstimateKey));
  if (saved && Number.isSafeInteger(saved.price) && saved.price >= 0 && Number.isFinite(saved.calculatedAt) && Date.now() - saved.calculatedAt < deliveryEstimateLifetime) selectedDeliveryEstimate = saved;
} catch {}

async function requestDeliveryRates(address, signal) {
  const parameters = new URLSearchParams();
  for (const key of ['zip', 'country', 'province']) parameters.set(`shipping_address[${key}]`, address[key] || '');
  const base = `${window.Shopify?.routes?.root || '/'}cart/`;
  const query = parameters.toString();
  let response = await fetch(`${base}prepare_shipping_rates.json?${query}`, { method: 'POST', headers: { Accept: 'application/json' }, signal });
  let data = await response.json();
  if (!response.ok) return { response, data };
  for (let attempt = 0; attempt < 8; attempt++) {
    if (attempt) await new Promise((resolve, reject) => {
      const abort = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
      const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, 500);
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) abort();
    });
    response = await fetch(`${base}async_shipping_rates.json?${query}`, { cache: 'no-store', headers: { Accept: 'application/json' }, signal });
    data = await response.json();
    if (!response.ok || data !== null) return { response, data };
  }
  throw new Error('Shipping rates are not ready.');
}

function deliveryMoney(currency) {
  const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', { style: 'currency', currency });
  return cents => formatter.format(cents / 100);
}

function deliveryCartSignature(cart) {
  return `${cart.currency}|${cart.total_price}|${Boolean(cart.taxes_included)}|${(cart.items || []).map(item => `${item.key}:${item.quantity}:${item.final_line_price};`).join('')}`;
}

function normaliseDeliveryRates(rates, currency) {
  if (!Array.isArray(rates) || !/^[A-Z]{3}$/.test(currency || '')) return [];
  return rates.flatMap(rate => {
    const raw = String(rate.price ?? '');
    if (!/^\d+(?:\.\d{1,2})?$/.test(raw) || (rate.currency && rate.currency !== currency)) return [];
    const price = Math.round(Number(raw) * 100);
    const name = String(rate.presentment_name || rate.name || rate.title || '').trim();
    return Number.isSafeInteger(price) && price >= 0 && name ? [{ price, name }] : [];
  }).sort((a, b) => a.price - b.price);
}

function calculateDeliveredTotal(subtotal, delivery, taxesIncluded) {
  if (![subtotal, delivery].every(value => Number.isSafeInteger(value) && value >= 0)) return null;
  const base = subtotal + delivery;
  if (!Number.isSafeInteger(base)) return null;
  const tax = taxesIncluded ? Math.round(base - base / 1.1) : Math.round(base * 0.1);
  const total = taxesIncluded ? base : base + tax;
  return Number.isSafeInteger(total) ? { delivery, tax, total } : null;
}

function announceDeliveryEstimate() {
  document.dispatchEvent(new CustomEvent('olouioo:delivery-estimate'));
}

function clearDeliveryEstimate() {
  selectedDeliveryEstimate = null;
  deliveryEstimateRevision++;
  try { sessionStorage.removeItem(deliveryEstimateKey); } catch {}
  announceDeliveryEstimate();
}

function setDeliveryEstimate(rate, cart, address) {
  if (!calculateDeliveredTotal(cart.total_price, rate.price, Boolean(cart.taxes_included))) return clearDeliveryEstimate();
  selectedDeliveryEstimate = { ...rate, signature: deliveryCartSignature(cart), currency: cart.currency, postcode: String(address.zip || ''), calculatedAt: Date.now() };
  try { sessionStorage.setItem(deliveryEstimateKey, JSON.stringify(selectedDeliveryEstimate)); } catch {}
  announceDeliveryEstimate();
}

class OlouiooDeliveredTotal extends HTMLElement {
  connectedCallback() {
    if (this._deliveryReady) return;
    this._deliveryReady = true;
    this._renderDelivery = () => this.renderDelivery();
    this._openDelivery = async () => {
      const summary = this.closest('.cart-drawer__summary');
      const dialog = document.getElementById('cart-drawer-shipping');
      if (summary && typeof dialog?.openDialog === 'function') await dialog.openDialog();
      const panel = summary ? dialog : this.closest('.cart__summary');
      const calculator = panel?.querySelector('cart-shipping-component');
      const details = calculator?.closest('details');
      if (details) details.open = true;
      calculator?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      calculator?.querySelector('[ref="zipInput"]')?.focus();
    };
    document.addEventListener('olouioo:delivery-estimate', this._renderDelivery);
    this.querySelector('[data-delivery-open]').addEventListener('click', this._openDelivery);
    this.renderDelivery();
  }
  disconnectedCallback() {
    document.removeEventListener('olouioo:delivery-estimate', this._renderDelivery);
    this.querySelector('[data-delivery-open]')?.removeEventListener('click', this._openDelivery);
    this._deliveryReady = false;
  }
  updatedCallback() { this.renderDelivery(); }
  renderDelivery() {
    const estimate = selectedDeliveryEstimate;
    const valid = estimate && Date.now() - estimate.calculatedAt < deliveryEstimateLifetime && estimate.signature === this.dataset.cartSignature && estimate.currency === this.dataset.currency;
    const totals = valid ? calculateDeliveredTotal(Number(this.dataset.subtotal), estimate.price, this.dataset.taxesIncluded === 'true') : null;
    this.querySelector('[data-delivery-breakdown]').hidden = !totals;
    this.querySelector('[data-delivered-total]').textContent = totals ? deliveryMoney(this.dataset.currency)(totals.total) : '—';
    this.querySelector('[data-delivery-total-status]').textContent = totals ? this.dataset.complete.replace('{postcode}', estimate.postcode).replace('{rate}', estimate.name) : this.dataset.empty;
    if (!totals) return;
    const money = deliveryMoney(this.dataset.currency);
    this.querySelector('[data-delivery-price]').textContent = money(totals.delivery);
    this.querySelector('[data-delivery-tax]').textContent = money(totals.tax);
    this.querySelector('[data-delivery-tax-label]').textContent = this.dataset.taxesIncluded === 'true' ? this.dataset.gstIncluded : this.dataset.gstEstimate;
  }
}

document.addEventListener(ThemeEvents.cartUpdate, () => {
  clearDeliveryEstimate();
  document.querySelectorAll('cart-shipping-component [ref="results"]').forEach(node => { node.hidden = true; });
});
if (!customElements.get('olouioo-delivered-total')) customElements.define('olouioo-delivered-total', OlouiooDeliveredTotal);

if (!customElements.get("cart-shipping-component")) {
  customElements.define("cart-shipping-component", CartShippingComponent);
}
